import { useEffect, useState } from 'react';
import { Loader2, Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useMediaPackagePreview } from '../api';

/** Roughly the harness' own sweep cadence — fast enough to read as motion. */
const FRAME_MS = 320;

/**
 * Preview for a .tfx package, played from its published golden frames.
 *
 * <p>There is no thumbnail, looping mp4 or sprite sheet anywhere in the
 * pipeline — the goldens (the harness' regression baseline) are the only
 * rendered artefact a package carries, and on-device picker tiles are rendered
 * live against the user's own footage, which nothing here can reproduce. So the
 * preview is a flip-book of 5-6 frames.
 */
export function PackagePreviewDialog({
  packageId,
  onClose,
}: {
  packageId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, isError, error } = useMediaPackagePreview(packageId);
  const [frame, setFrame] = useState(0);
  const [playing, setPlaying] = useState(true);

  const frames = data?.frameUrls ?? [];

  // Restart from the first frame whenever a different package is opened.
  useEffect(() => {
    setFrame(0);
    setPlaying(true);
  }, [packageId]);

  useEffect(() => {
    if (!playing || frames.length < 2) return;
    const timer = window.setInterval(
      () => setFrame((f) => (f + 1) % frames.length),
      FRAME_MS,
    );
    return () => window.clearInterval(timer);
  }, [playing, frames.length]);

  return (
    <Dialog open={!!packageId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-mono text-base">{packageId ?? ''}</DialogTitle>
          <DialogDescription>
            Published golden frames — the render engine&apos;s own regression baseline for this
            package.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex h-72 items-center justify-center">
            <Loader2 className="size-6 animate-spin text-primary" />
          </div>
        ) : isError ? (
          <div className="flex h-72 items-center justify-center px-6 text-center text-sm text-destructive">
            {error instanceof Error ? error.message : 'Failed to load the preview.'}
          </div>
        ) : frames.length === 0 ? (
          <div className="flex h-72 items-center justify-center px-6 text-center text-sm text-muted-foreground">
            This package published no golden frames, so there is nothing to preview.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex h-72 items-center justify-center overflow-hidden rounded-md border bg-black/40">
              {/* Every frame is mounted so the browser keeps them decoded —
                  swapping one <img> src per tick flickers on first pass. */}
              {frames.map((url, i) => (
                <img
                  key={url}
                  src={url}
                  alt={`Frame ${i + 1} of ${frames.length}`}
                  className={i === frame ? 'max-h-full max-w-full' : 'hidden'}
                />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => setPlaying((p) => !p)}>
                {playing ? <Pause className="size-4" /> : <Play className="size-4" />}
                {playing ? 'Pause' : 'Play'}
              </Button>
              <input
                type="range"
                min={0}
                max={frames.length - 1}
                value={frame}
                onChange={(e) => {
                  setPlaying(false);
                  setFrame(Number(e.target.value));
                }}
                className="flex-1"
                aria-label="Preview frame"
              />
              <span className="w-16 text-right text-sm tabular-nums text-muted-foreground">
                {frame + 1} / {frames.length}
              </span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
